/* tslint:disable */
/* eslint-disable */
/**
 * UpContent Platform API
 * # Introduction  The UpContent Public API provides access to UpContent's powerful content curation platform, allowing developers to build powerful content discovery and curation features directly into their applications.  # Authentication  The UpContent Public API uses OAuth2 authorization to grant applications access to UpContent user resources. A client application may request access to a single UpContent account or allow users to authorize access by that client to their UpContent resources.  ## User Authorization  UpContent uses a compliant OAuth2 flow to allow users to authorize access to their resources from third-party applications.  This implies that the access token must be requested from an authenticated client that can maintain the privacy of its credentials. In practice, this means that the access token must be requested from a server, rather than a JavaScript or mobile application.   ### Choosing a Grant Type The `authorization_code` is for applications intended to be use by multiple accounts where an user will grant access to their UpContent account to your application.  The `client_credentials` grant is designed for easy access to your own account without need for a full OAuth2 user authorization grant flow.  ### Choosing a Valid Scope  The UpContent API provides authorization scopes for read and write access based on the type of authorization grant requested. A valid scope must be chosen from the following:  Scope          | Grants                                     | Description ---------------|--------------------------------------------|------------ `client.read`  | `client_credentials`, `authorization_code` | Provides access to most public GET methods outlined in the documentation. `client.write` | `client_credentials`, `authorization_code` | Provides access to most public PUT, POST, and DElETE methods outlined in the documentation. `client.all`   | `client_credentials`, `authorization_code` | Alias for `client.read client.write`. `{module}.read`  | `authorization_code`                       | Provides access to a specific module's public GET methods outlined in the documentation. `{module}.write` | `authorization_code`                       | Provides access to a specific module's public PUT, POST, and DElETE methods outlined in the documentation. `{module}.all`   | `authorization_code`                       | Alias for `{module}.read {module}.write`.  Where `{module}` is one of the following: - team - folders - assignments - subscription - user - collections - collection_content - accounts - topics - topic_content - galleries - templates - quality  Currently module scopes are by special request only. By default when your client credentials are created they are given permission to use client.all.   ### Request Authorization  Begin by redirecting the user to the UpContent OAuth2 authorization page via the following request, with the querystring specified:  ``` GET https://accounts.upcontent.com/authorize  client_id={client_id}& response_type=code& grant_type=authorization_code& state={state}& redirect_uri={redirect_url}& scope={scope} ```  Parameter      | Description ---------------|--------------------------------------------------------------------- `client_id`    | The client identifier assigned to your client by UpContent. `grant_type`   | The OAuth2 grant type that is being used (`authorization_code`). `response_type`| The OAuth2 response type that is being used (`code`) `state`        | A value generated by the client that will be returned to the client to ensure that the authorization code that is returned is associated with an access token request. `redirect_uri` | One of the URLs previously associated with your client during registration with UpContent. The authorization code will be sent back to this URL. `scope`        | A list of space-delimited permissions for which the client is requesting authorization.  An allowed value for the `scope` parameter must be chosen from the table in the section *Choosing a Valid Scope*.  Upon successful authorization, an authorization code will be returned to the given redirect URL with the `state` parameter appended to the querystring, in addition to a `code` parameter which contains the authorization code.  ### Request an Access Token  Once a client has received an authorization code, it may then request an access token and a refresh token. The access token's purpose is to authenticate the client and user to the UpContent API. The purpose of the refresh token is to retrieve a new access token without the need to request a new authorization code.  The following endpoint may be used to request an access token, with parameters submitted as form data:  ``` POST https://accounts.upcontent.com/token  Content-Type: application/x-www-form-urlencoded  grant_type=authorization_code& code={code}& redirect_url={redirect_url}& client_id={client_id}& client_secret={client_secret} ```  Field           | Description ----------------|------------ `grant_type`    | The OAuth2 grant type used in the previous step (`authorization_code`) `code`          | The authorization code obtained in the previous step `redirect_uri`  | The redirect URL to which the code was sent `client_id`     | The client identifier assigned to your client by UpContent `client_secret` | The client secret assigned to your client by UpContent  If an access token is granted, the endpoint will return a **200 response** and a **JSON encoded object** described in the *Access Token* section.   ## Client-only Authorization  The UpContent API also provides a `client_credentials` grant for clients which only need access to a private UpContent account. This grant authorizes a client and generates an access token simultaneously on provision of the credentials generated when the client is registered with UpContent.  The following endpoint may be used to request an access token with this grant type, with parameters submitted as form data:  ``` POST https://accounts.upcontent.com/token  Content-Type: application/x-www-form-urlencoded  grant_type=client_credentials& client_id={client_id}& client_secret={client_secret}& scope={scope} ```  The fields described in the previous section have the same meaning here. The scope parameter here is optional, and if omitted will default to the value `client.all` (see *Choosing a Valid Scope*). If the request is successful, the endpoint will return a **200 response** and a **JSON encoded object** described in the *Access Token* section.  Use of the `client_credentials` grant type can only provide access tokens to the UpContent account of the client's owner.   ## Access Token  On a successful access token request, the UpContent API will return a **200 Response** and a **JSON-encoded object** with the following properties:  Property        | Type    | Description ----------------|---------|------------ `access_token`  | string  | The token used to access the UpContent API. `token_type`    | string  | The type of token. This will be 'bearer'. `expires_in`    | integer | The number of seconds until the token is expected to expire. `refresh_token` | string  | The token used to request a new access token.  The client will be responsible for securing the `access_token` and `refresh_token`, and for updating expired access tokens (see *Refreshing Access*). Note that from the date of issuance, the `access_token` will be valid for `expires_in` seconds and the `refresh_token` will be valid for 30 days.   ## Refreshing Access  Access tokens are generated with an expiration date, after which point they will no longer grant access to the UpContent API. When an access token expires, it can be refreshed using the `refresh_token` provided through the OAuth2 `refresh_token` grant.  This is accomplished making the following request, with parameters submitted as form data:  ``` POST https://accounts.upcontent.com/token  Content-Type: application/x-www-form-urlencoded  grant_type=refresh_token& refresh_token={refresh_token}& scope={scope} ```  The fields described in the previous section have the same meaning here; the field `refresh_token` should be set to the refresh token provided at the time the expired access token was provided. The scope parameter here is optional; if provided, it must be a equal to or a subset of the scope of the expired access token, and if omitted will default to the full scope of the expired token (see *Choosing a Valid Scope*). If the request is successful, the endpoint will return a new access and refresh token in the format defined above.  If both the access and refresh tokens for a user expire, the client must reobtain authorization from that user following the procedure described in *User Authorization*.   ## Accessing the UpContent API  Upon grant of an access token, it can be used until expiration to request data from or send data to the UpContent API endpoints with scopes for which the client is authorized. This is done by including the header ``` Authorization: Bearer {token} ``` in requests to the API, where {token} represents the access token. 
 *
 * OpenAPI spec version: 2.0.0
 * 
 *
 * NOTE: This class is auto generated by the swagger code generator program.
 * https://github.com/swagger-api/swagger-codegen.git
 * Do not edit the class manually.
 */
import globalAxios, { AxiosResponse, AxiosInstance, AxiosRequestConfig } from 'axios';
import { Configuration } from '../configuration';
// Some imports not used depending on template conditions
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, RequestArgs, BaseAPI, RequiredError } from '../base';
import { AccountIdCollectionsBody } from '../models';
import { Collection } from '../models';
import { CollectionIdAccountBody } from '../models';
import { CollectionIdDescriptionBody } from '../models';
import { CollectionIdNameBody } from '../models';
import { CollectionIdOwnerBody } from '../models';
import { CollectionIdParentBody } from '../models';
import { CollectionIdSuggestionsBody } from '../models';
import { CollectionURL } from '../models';
import { CollectionsBody } from '../models';
import { InlineResponse2001 } from '../models';
import { InlineResponse20010 } from '../models';
import { InlineResponse20110 } from '../models';
import { InlineResponse20111 } from '../models';
import { InlineResponse20112 } from '../models';
import { InlineResponse20113 } from '../models';
import { InlineResponse20114 } from '../models';
import { TagResults } from '../models';
import { UserURL } from '../models';
/**
 * CollectionsApi - axios parameter creator
 * @export
 */
export const CollectionsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Get a list of active collections in this account
         * @param {string} account_id UUID for the account
         * @param {string} [folder] UUID of the folder to get active collections from
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        accountsAccountIdActiveCollectionsGet: async (account_id: string, folder?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'account_id' is not null or undefined
            if (account_id === null || account_id === undefined) {
                throw new RequiredError('account_id','Required parameter account_id was null or undefined when calling accountsAccountIdActiveCollectionsGet.');
            }
            const localVarPath = `/accounts/{account_id}/active-collections`
                .replace(`{${"account_id"}}`, encodeURIComponent(String(account_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            if (folder !== undefined) {
                localVarQueryParameter['folder'] = folder;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get a list of collections for this account
         * @param {string} account_id UUID for the account
         * @param {string} [folder] UUID of the folder to get collections from
         * @param {boolean} [deactivated] If true, get only deactivated collections
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        accountsAccountIdCollectionsGet: async (account_id: string, folder?: string, deactivated?: boolean, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'account_id' is not null or undefined
            if (account_id === null || account_id === undefined) {
                throw new RequiredError('account_id','Required parameter account_id was null or undefined when calling accountsAccountIdCollectionsGet.');
            }
            const localVarPath = `/accounts/{account_id}/collections`
                .replace(`{${"account_id"}}`, encodeURIComponent(String(account_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            if (folder !== undefined) {
                localVarQueryParameter['folder'] = folder;
            }

            if (deactivated !== undefined) {
                localVarQueryParameter['deactivated'] = deactivated;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Create a collection in this account
         * @param {AccountIdCollectionsBody} body Data for creating a collection in this account
         * @param {string} account_id UUID for the account
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        accountsAccountIdCollectionsPost: async (body: AccountIdCollectionsBody, account_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling accountsAccountIdCollectionsPost.');
            }
            // verify required parameter 'account_id' is not null or undefined
            if (account_id === null || account_id === undefined) {
                throw new RequiredError('account_id','Required parameter account_id was null or undefined when calling accountsAccountIdCollectionsPost.');
            }
            const localVarPath = `/accounts/{account_id}/collections`
                .replace(`{${"account_id"}}`, encodeURIComponent(String(account_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Deactivate a collection
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activeCollectionsCollectionIdDelete: async (collection_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'collection_id' is not null or undefined
            if (collection_id === null || collection_id === undefined) {
                throw new RequiredError('collection_id','Required parameter collection_id was null or undefined when calling activeCollectionsCollectionIdDelete.');
            }
            const localVarPath = `/active-collections/{collection_id}`
                .replace(`{${"collection_id"}}`, encodeURIComponent(String(collection_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Activate a collection
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activeCollectionsCollectionIdPut: async (collection_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'collection_id' is not null or undefined
            if (collection_id === null || collection_id === undefined) {
                throw new RequiredError('collection_id','Required parameter collection_id was null or undefined when calling activeCollectionsCollectionIdPut.');
            }
            const localVarPath = `/active-collections/{collection_id}`
                .replace(`{${"collection_id"}}`, encodeURIComponent(String(collection_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Transfer the collection to a different account
         * @param {CollectionIdAccountBody} body Data for transferring a collection to a different account
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        collectionsCollectionIdAccountPut: async (body: CollectionIdAccountBody, collection_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling collectionsCollectionIdAccountPut.');
            }
            // verify required parameter 'collection_id' is not null or undefined
            if (collection_id === null || collection_id === undefined) {
                throw new RequiredError('collection_id','Required parameter collection_id was null or undefined when calling collectionsCollectionIdAccountPut.');
            }
            const localVarPath = `/collections/{collection_id}/account`
                .replace(`{${"collection_id"}}`, encodeURIComponent(String(collection_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        collectionsCollectionIdContentCampaignsPost: async (collection_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'collection_id' is not null or undefined
            if (collection_id === null || collection_id === undefined) {
                throw new RequiredError('collection_id','Required parameter collection_id was null or undefined when calling collectionsCollectionIdContentCampaignsPost.');
            }
            const localVarPath = `/collections/{collection_id}/content-campaigns`
                .replace(`{${"collection_id"}}`, encodeURIComponent(String(collection_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Delete a collection
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        collectionsCollectionIdDelete: async (collection_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'collection_id' is not null or undefined
            if (collection_id === null || collection_id === undefined) {
                throw new RequiredError('collection_id','Required parameter collection_id was null or undefined when calling collectionsCollectionIdDelete.');
            }
            const localVarPath = `/collections/{collection_id}`
                .replace(`{${"collection_id"}}`, encodeURIComponent(String(collection_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Update the collection description
         * @param {CollectionIdDescriptionBody} body Data for updating the collection description
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        collectionsCollectionIdDescriptionPut: async (body: CollectionIdDescriptionBody, collection_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling collectionsCollectionIdDescriptionPut.');
            }
            // verify required parameter 'collection_id' is not null or undefined
            if (collection_id === null || collection_id === undefined) {
                throw new RequiredError('collection_id','Required parameter collection_id was null or undefined when calling collectionsCollectionIdDescriptionPut.');
            }
            const localVarPath = `/collections/{collection_id}/description`
                .replace(`{${"collection_id"}}`, encodeURIComponent(String(collection_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get a collection
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        collectionsCollectionIdGet: async (collection_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'collection_id' is not null or undefined
            if (collection_id === null || collection_id === undefined) {
                throw new RequiredError('collection_id','Required parameter collection_id was null or undefined when calling collectionsCollectionIdGet.');
            }
            const localVarPath = `/collections/{collection_id}`
                .replace(`{${"collection_id"}}`, encodeURIComponent(String(collection_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Update the collection name
         * @param {CollectionIdNameBody} body Data for updating the collection name
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        collectionsCollectionIdNamePut: async (body: CollectionIdNameBody, collection_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling collectionsCollectionIdNamePut.');
            }
            // verify required parameter 'collection_id' is not null or undefined
            if (collection_id === null || collection_id === undefined) {
                throw new RequiredError('collection_id','Required parameter collection_id was null or undefined when calling collectionsCollectionIdNamePut.');
            }
            const localVarPath = `/collections/{collection_id}/name`
                .replace(`{${"collection_id"}}`, encodeURIComponent(String(collection_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Update the collection owner
         * @param {CollectionIdOwnerBody} body Data for updating the collection owner
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        collectionsCollectionIdOwnerPut: async (body: CollectionIdOwnerBody, collection_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling collectionsCollectionIdOwnerPut.');
            }
            // verify required parameter 'collection_id' is not null or undefined
            if (collection_id === null || collection_id === undefined) {
                throw new RequiredError('collection_id','Required parameter collection_id was null or undefined when calling collectionsCollectionIdOwnerPut.');
            }
            const localVarPath = `/collections/{collection_id}/owner`
                .replace(`{${"collection_id"}}`, encodeURIComponent(String(collection_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Move the collection to a new folder
         * @param {CollectionIdParentBody} body Data for updating the collection&#x27;s parent folder
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        collectionsCollectionIdParentPut: async (body: CollectionIdParentBody, collection_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling collectionsCollectionIdParentPut.');
            }
            // verify required parameter 'collection_id' is not null or undefined
            if (collection_id === null || collection_id === undefined) {
                throw new RequiredError('collection_id','Required parameter collection_id was null or undefined when calling collectionsCollectionIdParentPut.');
            }
            const localVarPath = `/collections/{collection_id}/parent`
                .replace(`{${"collection_id"}}`, encodeURIComponent(String(collection_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Add suggestions to this collection
         * @param {CollectionIdSuggestionsBody} body Suggestions to add to the collection
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        collectionsCollectionIdSuggestionsPost: async (body: CollectionIdSuggestionsBody, collection_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling collectionsCollectionIdSuggestionsPost.');
            }
            // verify required parameter 'collection_id' is not null or undefined
            if (collection_id === null || collection_id === undefined) {
                throw new RequiredError('collection_id','Required parameter collection_id was null or undefined when calling collectionsCollectionIdSuggestionsPost.');
            }
            const localVarPath = `/collections/{collection_id}/suggestions`
                .replace(`{${"collection_id"}}`, encodeURIComponent(String(collection_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get tags for all articles in collection
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        collectionsCollectionIdTagsGet: async (collection_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'collection_id' is not null or undefined
            if (collection_id === null || collection_id === undefined) {
                throw new RequiredError('collection_id','Required parameter collection_id was null or undefined when calling collectionsCollectionIdTagsGet.');
            }
            const localVarPath = `/collections/{collection_id}/tags`
                .replace(`{${"collection_id"}}`, encodeURIComponent(String(collection_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get the list of users which can access the collection
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        collectionsCollectionIdUsersGet: async (collection_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'collection_id' is not null or undefined
            if (collection_id === null || collection_id === undefined) {
                throw new RequiredError('collection_id','Required parameter collection_id was null or undefined when calling collectionsCollectionIdUsersGet.');
            }
            const localVarPath = `/collections/{collection_id}/users`
                .replace(`{${"collection_id"}}`, encodeURIComponent(String(collection_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get the list of the current user's collections
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        collectionsGet: async (options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/collections`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Create a collection
         * @param {CollectionsBody} body Data for creating a collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        collectionsPost: async (body: CollectionsBody, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling collectionsPost.');
            }
            const localVarPath = `/collections`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get entities for some text
         * @param {string} text Text to extract entities from
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        entitiesExtractGet: async (text: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'text' is not null or undefined
            if (text === null || text === undefined) {
                throw new RequiredError('text','Required parameter text was null or undefined when calling entitiesExtractGet.');
            }
            const localVarPath = `/entities/extract`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (text !== undefined) {
                localVarQueryParameter['text'] = text;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get the policy for a set of collections
         * @param {Array<string>} collections Collections to enforce policy from, will use
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        policyGet: async (collections: Array<string>, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'collections' is not null or undefined
            if (collections === null || collections === undefined) {
                throw new RequiredError('collections','Required parameter collections was null or undefined when calling policyGet.');
            }
            const localVarPath = `/policy`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (collections) {
                localVarQueryParameter['collections'] = collections.join(COLLECTION_FORMATS.csv);
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * CollectionsApi - functional programming interface
 * @export
 */
export const CollectionsApiFp = function(configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Get a list of active collections in this account
         * @param {string} account_id UUID for the account
         * @param {string} [folder] UUID of the folder to get active collections from
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdActiveCollectionsGet(account_id: string, folder?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Array<CollectionURL>>>> {
            const localVarAxiosArgs = await CollectionsApiAxiosParamCreator(configuration).accountsAccountIdActiveCollectionsGet(account_id, folder, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get a list of collections for this account
         * @param {string} account_id UUID for the account
         * @param {string} [folder] UUID of the folder to get collections from
         * @param {boolean} [deactivated] If true, get only deactivated collections
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdCollectionsGet(account_id: string, folder?: string, deactivated?: boolean, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Array<CollectionURL>>>> {
            const localVarAxiosArgs = await CollectionsApiAxiosParamCreator(configuration).accountsAccountIdCollectionsGet(account_id, folder, deactivated, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Create a collection in this account
         * @param {AccountIdCollectionsBody} body Data for creating a collection in this account
         * @param {string} account_id UUID for the account
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdCollectionsPost(body: AccountIdCollectionsBody, account_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Collection>>> {
            const localVarAxiosArgs = await CollectionsApiAxiosParamCreator(configuration).accountsAccountIdCollectionsPost(body, account_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Deactivate a collection
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activeCollectionsCollectionIdDelete(collection_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await CollectionsApiAxiosParamCreator(configuration).activeCollectionsCollectionIdDelete(collection_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Activate a collection
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activeCollectionsCollectionIdPut(collection_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await CollectionsApiAxiosParamCreator(configuration).activeCollectionsCollectionIdPut(collection_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Transfer the collection to a different account
         * @param {CollectionIdAccountBody} body Data for transferring a collection to a different account
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdAccountPut(body: CollectionIdAccountBody, collection_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<InlineResponse20111>>> {
            const localVarAxiosArgs = await CollectionsApiAxiosParamCreator(configuration).collectionsCollectionIdAccountPut(body, collection_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdContentCampaignsPost(collection_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<any>>> {
            const localVarAxiosArgs = await CollectionsApiAxiosParamCreator(configuration).collectionsCollectionIdContentCampaignsPost(collection_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Delete a collection
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdDelete(collection_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await CollectionsApiAxiosParamCreator(configuration).collectionsCollectionIdDelete(collection_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Update the collection description
         * @param {CollectionIdDescriptionBody} body Data for updating the collection description
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdDescriptionPut(body: CollectionIdDescriptionBody, collection_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<InlineResponse20114>>> {
            const localVarAxiosArgs = await CollectionsApiAxiosParamCreator(configuration).collectionsCollectionIdDescriptionPut(body, collection_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get a collection
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdGet(collection_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Collection>>> {
            const localVarAxiosArgs = await CollectionsApiAxiosParamCreator(configuration).collectionsCollectionIdGet(collection_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Update the collection name
         * @param {CollectionIdNameBody} body Data for updating the collection name
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdNamePut(body: CollectionIdNameBody, collection_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<InlineResponse20113>>> {
            const localVarAxiosArgs = await CollectionsApiAxiosParamCreator(configuration).collectionsCollectionIdNamePut(body, collection_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Update the collection owner
         * @param {CollectionIdOwnerBody} body Data for updating the collection owner
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdOwnerPut(body: CollectionIdOwnerBody, collection_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<InlineResponse20112>>> {
            const localVarAxiosArgs = await CollectionsApiAxiosParamCreator(configuration).collectionsCollectionIdOwnerPut(body, collection_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Move the collection to a new folder
         * @param {CollectionIdParentBody} body Data for updating the collection&#x27;s parent folder
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdParentPut(body: CollectionIdParentBody, collection_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<InlineResponse20110>>> {
            const localVarAxiosArgs = await CollectionsApiAxiosParamCreator(configuration).collectionsCollectionIdParentPut(body, collection_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Add suggestions to this collection
         * @param {CollectionIdSuggestionsBody} body Suggestions to add to the collection
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdSuggestionsPost(body: CollectionIdSuggestionsBody, collection_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<InlineResponse20010>>> {
            const localVarAxiosArgs = await CollectionsApiAxiosParamCreator(configuration).collectionsCollectionIdSuggestionsPost(body, collection_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get tags for all articles in collection
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdTagsGet(collection_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<TagResults>>> {
            const localVarAxiosArgs = await CollectionsApiAxiosParamCreator(configuration).collectionsCollectionIdTagsGet(collection_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get the list of users which can access the collection
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdUsersGet(collection_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Array<UserURL>>>> {
            const localVarAxiosArgs = await CollectionsApiAxiosParamCreator(configuration).collectionsCollectionIdUsersGet(collection_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get the list of the current user's collections
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsGet(options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Array<CollectionURL>>>> {
            const localVarAxiosArgs = await CollectionsApiAxiosParamCreator(configuration).collectionsGet(options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Create a collection
         * @param {CollectionsBody} body Data for creating a collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsPost(body: CollectionsBody, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Collection>>> {
            const localVarAxiosArgs = await CollectionsApiAxiosParamCreator(configuration).collectionsPost(body, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get entities for some text
         * @param {string} text Text to extract entities from
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async entitiesExtractGet(text: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<InlineResponse2001>>> {
            const localVarAxiosArgs = await CollectionsApiAxiosParamCreator(configuration).entitiesExtractGet(text, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get the policy for a set of collections
         * @param {Array<string>} collections Collections to enforce policy from, will use
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async policyGet(collections: Array<string>, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<InlineResponse2001>>> {
            const localVarAxiosArgs = await CollectionsApiAxiosParamCreator(configuration).policyGet(collections, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
    }
};

/**
 * CollectionsApi - factory interface
 * @export
 */
export const CollectionsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    return {
        /**
         * 
         * @summary Get a list of active collections in this account
         * @param {string} account_id UUID for the account
         * @param {string} [folder] UUID of the folder to get active collections from
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdActiveCollectionsGet(account_id: string, folder?: string, options?: AxiosRequestConfig): Promise<AxiosResponse<Array<CollectionURL>>> {
            return CollectionsApiFp(configuration).accountsAccountIdActiveCollectionsGet(account_id, folder, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get a list of collections for this account
         * @param {string} account_id UUID for the account
         * @param {string} [folder] UUID of the folder to get collections from
         * @param {boolean} [deactivated] If true, get only deactivated collections
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdCollectionsGet(account_id: string, folder?: string, deactivated?: boolean, options?: AxiosRequestConfig): Promise<AxiosResponse<Array<CollectionURL>>> {
            return CollectionsApiFp(configuration).accountsAccountIdCollectionsGet(account_id, folder, deactivated, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Create a collection in this account
         * @param {AccountIdCollectionsBody} body Data for creating a collection in this account
         * @param {string} account_id UUID for the account
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdCollectionsPost(body: AccountIdCollectionsBody, account_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<Collection>> {
            return CollectionsApiFp(configuration).accountsAccountIdCollectionsPost(body, account_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Deactivate a collection
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activeCollectionsCollectionIdDelete(collection_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return CollectionsApiFp(configuration).activeCollectionsCollectionIdDelete(collection_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Activate a collection
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activeCollectionsCollectionIdPut(collection_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return CollectionsApiFp(configuration).activeCollectionsCollectionIdPut(collection_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Transfer the collection to a different account
         * @param {CollectionIdAccountBody} body Data for transferring a collection to a different account
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdAccountPut(body: CollectionIdAccountBody, collection_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<InlineResponse20111>> {
            return CollectionsApiFp(configuration).collectionsCollectionIdAccountPut(body, collection_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdContentCampaignsPost(collection_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<any>> {
            return CollectionsApiFp(configuration).collectionsCollectionIdContentCampaignsPost(collection_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Delete a collection
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdDelete(collection_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return CollectionsApiFp(configuration).collectionsCollectionIdDelete(collection_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Update the collection description
         * @param {CollectionIdDescriptionBody} body Data for updating the collection description
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdDescriptionPut(body: CollectionIdDescriptionBody, collection_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<InlineResponse20114>> {
            return CollectionsApiFp(configuration).collectionsCollectionIdDescriptionPut(body, collection_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get a collection
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdGet(collection_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<Collection>> {
            return CollectionsApiFp(configuration).collectionsCollectionIdGet(collection_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Update the collection name
         * @param {CollectionIdNameBody} body Data for updating the collection name
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdNamePut(body: CollectionIdNameBody, collection_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<InlineResponse20113>> {
            return CollectionsApiFp(configuration).collectionsCollectionIdNamePut(body, collection_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Update the collection owner
         * @param {CollectionIdOwnerBody} body Data for updating the collection owner
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdOwnerPut(body: CollectionIdOwnerBody, collection_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<InlineResponse20112>> {
            return CollectionsApiFp(configuration).collectionsCollectionIdOwnerPut(body, collection_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Move the collection to a new folder
         * @param {CollectionIdParentBody} body Data for updating the collection&#x27;s parent folder
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdParentPut(body: CollectionIdParentBody, collection_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<InlineResponse20110>> {
            return CollectionsApiFp(configuration).collectionsCollectionIdParentPut(body, collection_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Add suggestions to this collection
         * @param {CollectionIdSuggestionsBody} body Suggestions to add to the collection
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdSuggestionsPost(body: CollectionIdSuggestionsBody, collection_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<InlineResponse20010>> {
            return CollectionsApiFp(configuration).collectionsCollectionIdSuggestionsPost(body, collection_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get tags for all articles in collection
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdTagsGet(collection_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<TagResults>> {
            return CollectionsApiFp(configuration).collectionsCollectionIdTagsGet(collection_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get the list of users which can access the collection
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdUsersGet(collection_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<Array<UserURL>>> {
            return CollectionsApiFp(configuration).collectionsCollectionIdUsersGet(collection_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get the list of the current user's collections
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsGet(options?: AxiosRequestConfig): Promise<AxiosResponse<Array<CollectionURL>>> {
            return CollectionsApiFp(configuration).collectionsGet(options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Create a collection
         * @param {CollectionsBody} body Data for creating a collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsPost(body: CollectionsBody, options?: AxiosRequestConfig): Promise<AxiosResponse<Collection>> {
            return CollectionsApiFp(configuration).collectionsPost(body, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get entities for some text
         * @param {string} text Text to extract entities from
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async entitiesExtractGet(text: string, options?: AxiosRequestConfig): Promise<AxiosResponse<InlineResponse2001>> {
            return CollectionsApiFp(configuration).entitiesExtractGet(text, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get the policy for a set of collections
         * @param {Array<string>} collections Collections to enforce policy from, will use
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async policyGet(collections: Array<string>, options?: AxiosRequestConfig): Promise<AxiosResponse<InlineResponse2001>> {
            return CollectionsApiFp(configuration).policyGet(collections, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * CollectionsApi - object-oriented interface
 * @export
 * @class CollectionsApi
 * @extends {BaseAPI}
 */
export class CollectionsApi extends BaseAPI {
    /**
     * 
     * @summary Get a list of active collections in this account
     * @param {string} account_id UUID for the account
     * @param {string} [folder] UUID of the folder to get active collections from
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CollectionsApi
     */
    public async accountsAccountIdActiveCollectionsGet(account_id: string, folder?: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<Array<CollectionURL>>> {
        return CollectionsApiFp(this.configuration).accountsAccountIdActiveCollectionsGet(account_id, folder, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get a list of collections for this account
     * @param {string} account_id UUID for the account
     * @param {string} [folder] UUID of the folder to get collections from
     * @param {boolean} [deactivated] If true, get only deactivated collections
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CollectionsApi
     */
    public async accountsAccountIdCollectionsGet(account_id: string, folder?: string, deactivated?: boolean, options?: AxiosRequestConfig) : Promise<AxiosResponse<Array<CollectionURL>>> {
        return CollectionsApiFp(this.configuration).accountsAccountIdCollectionsGet(account_id, folder, deactivated, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Create a collection in this account
     * @param {AccountIdCollectionsBody} body Data for creating a collection in this account
     * @param {string} account_id UUID for the account
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CollectionsApi
     */
    public async accountsAccountIdCollectionsPost(body: AccountIdCollectionsBody, account_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<Collection>> {
        return CollectionsApiFp(this.configuration).accountsAccountIdCollectionsPost(body, account_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Deactivate a collection
     * @param {string} collection_id UUID for the collection
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CollectionsApi
     */
    public async activeCollectionsCollectionIdDelete(collection_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return CollectionsApiFp(this.configuration).activeCollectionsCollectionIdDelete(collection_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Activate a collection
     * @param {string} collection_id UUID for the collection
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CollectionsApi
     */
    public async activeCollectionsCollectionIdPut(collection_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return CollectionsApiFp(this.configuration).activeCollectionsCollectionIdPut(collection_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Transfer the collection to a different account
     * @param {CollectionIdAccountBody} body Data for transferring a collection to a different account
     * @param {string} collection_id UUID for the collection
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CollectionsApi
     */
    public async collectionsCollectionIdAccountPut(body: CollectionIdAccountBody, collection_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<InlineResponse20111>> {
        return CollectionsApiFp(this.configuration).collectionsCollectionIdAccountPut(body, collection_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @param {string} collection_id UUID for the collection
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CollectionsApi
     */
    public async collectionsCollectionIdContentCampaignsPost(collection_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<any>> {
        return CollectionsApiFp(this.configuration).collectionsCollectionIdContentCampaignsPost(collection_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Delete a collection
     * @param {string} collection_id UUID for the collection
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CollectionsApi
     */
    public async collectionsCollectionIdDelete(collection_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return CollectionsApiFp(this.configuration).collectionsCollectionIdDelete(collection_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Update the collection description
     * @param {CollectionIdDescriptionBody} body Data for updating the collection description
     * @param {string} collection_id UUID for the collection
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CollectionsApi
     */
    public async collectionsCollectionIdDescriptionPut(body: CollectionIdDescriptionBody, collection_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<InlineResponse20114>> {
        return CollectionsApiFp(this.configuration).collectionsCollectionIdDescriptionPut(body, collection_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get a collection
     * @param {string} collection_id UUID for the collection
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CollectionsApi
     */
    public async collectionsCollectionIdGet(collection_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<Collection>> {
        return CollectionsApiFp(this.configuration).collectionsCollectionIdGet(collection_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Update the collection name
     * @param {CollectionIdNameBody} body Data for updating the collection name
     * @param {string} collection_id UUID for the collection
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CollectionsApi
     */
    public async collectionsCollectionIdNamePut(body: CollectionIdNameBody, collection_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<InlineResponse20113>> {
        return CollectionsApiFp(this.configuration).collectionsCollectionIdNamePut(body, collection_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Update the collection owner
     * @param {CollectionIdOwnerBody} body Data for updating the collection owner
     * @param {string} collection_id UUID for the collection
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CollectionsApi
     */
    public async collectionsCollectionIdOwnerPut(body: CollectionIdOwnerBody, collection_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<InlineResponse20112>> {
        return CollectionsApiFp(this.configuration).collectionsCollectionIdOwnerPut(body, collection_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Move the collection to a new folder
     * @param {CollectionIdParentBody} body Data for updating the collection&#x27;s parent folder
     * @param {string} collection_id UUID for the collection
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CollectionsApi
     */
    public async collectionsCollectionIdParentPut(body: CollectionIdParentBody, collection_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<InlineResponse20110>> {
        return CollectionsApiFp(this.configuration).collectionsCollectionIdParentPut(body, collection_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Add suggestions to this collection
     * @param {CollectionIdSuggestionsBody} body Suggestions to add to the collection
     * @param {string} collection_id UUID for the collection
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CollectionsApi
     */
    public async collectionsCollectionIdSuggestionsPost(body: CollectionIdSuggestionsBody, collection_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<InlineResponse20010>> {
        return CollectionsApiFp(this.configuration).collectionsCollectionIdSuggestionsPost(body, collection_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get tags for all articles in collection
     * @param {string} collection_id UUID for the collection
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CollectionsApi
     */
    public async collectionsCollectionIdTagsGet(collection_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<TagResults>> {
        return CollectionsApiFp(this.configuration).collectionsCollectionIdTagsGet(collection_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get the list of users which can access the collection
     * @param {string} collection_id UUID for the collection
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CollectionsApi
     */
    public async collectionsCollectionIdUsersGet(collection_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<Array<UserURL>>> {
        return CollectionsApiFp(this.configuration).collectionsCollectionIdUsersGet(collection_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get the list of the current user's collections
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CollectionsApi
     */
    public async collectionsGet(options?: AxiosRequestConfig) : Promise<AxiosResponse<Array<CollectionURL>>> {
        return CollectionsApiFp(this.configuration).collectionsGet(options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Create a collection
     * @param {CollectionsBody} body Data for creating a collection
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CollectionsApi
     */
    public async collectionsPost(body: CollectionsBody, options?: AxiosRequestConfig) : Promise<AxiosResponse<Collection>> {
        return CollectionsApiFp(this.configuration).collectionsPost(body, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get entities for some text
     * @param {string} text Text to extract entities from
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CollectionsApi
     */
    public async entitiesExtractGet(text: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<InlineResponse2001>> {
        return CollectionsApiFp(this.configuration).entitiesExtractGet(text, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get the policy for a set of collections
     * @param {Array<string>} collections Collections to enforce policy from, will use
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CollectionsApi
     */
    public async policyGet(collections: Array<string>, options?: AxiosRequestConfig) : Promise<AxiosResponse<InlineResponse2001>> {
        return CollectionsApiFp(this.configuration).policyGet(collections, options).then((request) => request(this.axios, this.basePath));
    }
}
